/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getAccountingCurrencies } from 'app/entities/accounting-currency/accounting-currency.reducer';
import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getAccountingTransactions } from 'app/entities/accounting-transaction/accounting-transaction.reducer';
import { AddedByType } from 'app/shared/model/enumerations/added-by-type.model';
import { createEntity, getEntity, reset, updateEntity } from './accounting-expense-ledger.reducer';

export const AccountingExpenseLedgerUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const accountingCurrencies = useAppSelector(state => state.accountingCurrency.entities);
  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const accountingTransactions = useAppSelector(state => state.accountingTransaction.entities);
  const accountingExpenseLedgerEntity = useAppSelector(state => state.accountingExpenseLedger.entity);
  const loading = useAppSelector(state => state.accountingExpenseLedger.loading);
  const updating = useAppSelector(state => state.accountingExpenseLedger.updating);
  const updateSuccess = useAppSelector(state => state.accountingExpenseLedger.updateSuccess);
  const addedByTypeValues = Object.keys(AddedByType);

  const handleClose = () => {
    navigate(`/accounting-expense-ledger${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getAccountingCurrencies({}));
    dispatch(getClinicalDictionaries({}));
    dispatch(getAccountingTransactions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.amount !== undefined && typeof values.amount !== 'number') {
      values.amount = Number(values.amount);
    }
    if (values.remarks !== undefined && typeof values.remarks !== 'number') {
      values.remarks = Number(values.remarks);
    }
    if (values.currencyRate !== undefined && typeof values.currencyRate !== 'number') {
      values.currencyRate = Number(values.currencyRate);
    }
    if (values.pendingAmount !== undefined && typeof values.pendingAmount !== 'number') {
      values.pendingAmount = Number(values.pendingAmount);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...accountingExpenseLedgerEntity,
      ...values,
      baseCurrency: accountingCurrencies.find(it => it.id.toString() === values.baseCurrency?.toString()),
      category: clinicalDictionaries.find(it => it.id.toString() === values.category?.toString()),
      entityType: clinicalDictionaries.find(it => it.id.toString() === values.entityType?.toString()),
      accountTransaction: accountingTransactions.find(it => it.id.toString() === values.accountTransaction?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          transactedByType: 'ACCOUNTANT',
          ...accountingExpenseLedgerEntity,
          baseCurrency: accountingExpenseLedgerEntity?.baseCurrency?.id,
          category: accountingExpenseLedgerEntity?.category?.id,
          entityType: accountingExpenseLedgerEntity?.entityType?.id,
          accountTransaction: accountingExpenseLedgerEntity?.accountTransaction?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.accountingExpenseLedger.home.createOrEditLabel" data-cy="AccountingExpenseLedgerCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.home.createOrEditLabel">
              Create or edit a AccountingExpenseLedger
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="accounting-expense-ledger-id"
                  label={translate('tinyMedicRestApp.accountingExpenseLedger.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.ledgerNo')}
                id="accounting-expense-ledger-ledgerNo"
                name="ledgerNo"
                data-cy="ledgerNo"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <UncontrolledTooltip target="ledgerNoLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.ledgerNo" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.amount')}
                id="accounting-expense-ledger-amount"
                name="amount"
                data-cy="amount"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="amountLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.amount" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.remarks')}
                id="accounting-expense-ledger-remarks"
                name="remarks"
                data-cy="remarks"
                type="text"
              />
              <UncontrolledTooltip target="remarksLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.remarks" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.currencyRate')}
                id="accounting-expense-ledger-currencyRate"
                name="currencyRate"
                data-cy="currencyRate"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="currencyRateLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.currencyRate" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.settled')}
                id="accounting-expense-ledger-settled"
                name="settled"
                data-cy="settled"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="settledLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.settled" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.pendingAmount')}
                id="accounting-expense-ledger-pendingAmount"
                name="pendingAmount"
                data-cy="pendingAmount"
                type="text"
              />
              <UncontrolledTooltip target="pendingAmountLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.pendingAmount" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.transactedById')}
                id="accounting-expense-ledger-transactedById"
                name="transactedById"
                data-cy="transactedById"
                type="text"
              />
              <UncontrolledTooltip target="transactedByIdLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.transactedById" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.transactedByType')}
                id="accounting-expense-ledger-transactedByType"
                name="transactedByType"
                data-cy="transactedByType"
                type="select"
              >
                {addedByTypeValues.map(addedByType => (
                  <option value={addedByType} key={addedByType}>
                    {translate(`tinyMedicRestApp.AddedByType.${addedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <UncontrolledTooltip target="transactedByTypeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.transactedByType" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.closed')}
                id="accounting-expense-ledger-closed"
                name="closed"
                data-cy="closed"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="closedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.closed" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.entityId')}
                id="accounting-expense-ledger-entityId"
                name="entityId"
                data-cy="entityId"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.active')}
                id="accounting-expense-ledger-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.cursor')}
                id="accounting-expense-ledger-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.lastUpdated')}
                id="accounting-expense-ledger-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.microSec')}
                id="accounting-expense-ledger-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingExpenseLedger.timestampEntry')}
                id="accounting-expense-ledger-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.accountingExpenseLedger.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="accounting-expense-ledger-baseCurrency"
                name="baseCurrency"
                data-cy="baseCurrency"
                label={translate('tinyMedicRestApp.accountingExpenseLedger.baseCurrency')}
                type="select"
              >
                <option value="" key="0" />
                {accountingCurrencies
                  ? accountingCurrencies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-expense-ledger-category"
                name="category"
                data-cy="category"
                label={translate('tinyMedicRestApp.accountingExpenseLedger.category')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-expense-ledger-entityType"
                name="entityType"
                data-cy="entityType"
                label={translate('tinyMedicRestApp.accountingExpenseLedger.entityType')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-expense-ledger-accountTransaction"
                name="accountTransaction"
                data-cy="accountTransaction"
                label={translate('tinyMedicRestApp.accountingExpenseLedger.accountTransaction')}
                type="select"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/accounting-expense-ledger" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AccountingExpenseLedgerUpdate;
